import React, { useEffect } from 'react';
import { Box, Button, TextField } from '@flos/react-ui';
import useAppState from '../../state/appState';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import {
    GroupOfTextfields as ListOfItemsType,
    FormValues,
    FormConfig
} from '@topdanmark-onlinesolutions/claim-form-types';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getValue, getValueOrDefault } from '../values/Get';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

type Values = {
    name: string;
    label: string;
    value: string;
}[][];

const GroupOfTextFields = ({
    elementId,
    caption,
    maxGroups,
    addButtonText,
    removeButtonText,
    emptyListText,
    textfields
}: ListOfItemsType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const values: Values = GroupOfTextFields.getValueOrDefault(formValues, elementId);
    const [validationResult, setValidationResult] = React.useState<boolean[][]>([]);
    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = React.useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const values: Values = getValueOrDefault(formConfig, formValues, elementId);

        const result = values.map((valueList) =>
            valueList.map((item, i) => (textfields[i].required && item.value === '' ? false : true))
        );
        setValidationResult(result);
    };

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [values]);

    useEffect(() => {
        if (values.length === 0) {
            onAddField();
        }
        // eslint-disable-next-line
    }, []);

    const onChangeValue = (
        valueIndex: number,
        fieldIndex: number,
        name: string,
        label: string,
        changedValue: string
    ) => {
        const values: Values = GroupOfTextFields.getValueOrDefault(formValues, elementId);
        values[valueIndex][fieldIndex] = { name, label, value: changedValue };
        setFormValue(elementId, values);
    };

    const onRemoveField = (index: number) => {
        const values: Values = GroupOfTextFields.getValueOrDefault(formValues, elementId);
        values.splice(index, 1);
        setFormValue(elementId, values);
    };

    const onAddField = () => {
        const values: Values = GroupOfTextFields.getValueOrDefault(formValues, elementId);
        values.push(textfields.map(() => ({ name: '', label: '', value: '' })));
        setFormValue(elementId, values);
    };

    return (
        <>
            {caption && <h6>{caption}</h6>}
            {values.length === 0 && emptyListText && <p>{emptyListText}</p>}
            {values.map((value, valueIndex) => (
                <Box key={`box-${valueIndex}`} className="u-spacing-stack-bottom-5 list-of-items-field">
                    {textfields.map((field, fieldIndex) => (
                        <TextField
                            key={`textfield-${valueIndex}-${fieldIndex}`}
                            label={field.placeholder}
                            value={value[fieldIndex].value}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                onChangeValue(
                                    valueIndex,
                                    fieldIndex,
                                    field.name,
                                    field.placeholder,
                                    event?.target.value
                                )
                            }
                            maxLength={field.maxLength}
                            required={field.required}
                            onBlur={() => {
                                doValidation();
                            }}
                            onFocus={() => {
                                setFieldHasHadFocusAtLeastOnce(true);
                            }}
                            errorText="Skriv noget i feltet"
                            isValid={
                                validationResult[valueIndex] !== undefined
                                    ? validationResult[valueIndex][fieldIndex]
                                    : true
                            }
                        />
                    ))}
                    {valueIndex > 0 ? (
                        <Button
                            key={`button-${valueIndex}`}
                            theme="secondary"
                            onClick={() => onRemoveField(valueIndex)}
                        >
                            {removeButtonText}
                        </Button>
                    ) : null}
                </Box>
            ))}
            {values.length < maxGroups && <Button onClick={() => onAddField()}>{addButtonText}</Button>}
        </>
    );
};

GroupOfTextFields.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as ListOfItemsType;
    const { summaryCaption } = element;

    const values: Values = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (values === undefined) {
        return <></>;
    }

    const allFieldsEmpty = values.every((value) => value.every((item) => !item.value));
    const listOfValues = allFieldsEmpty ? (
        <p>{element.emptyListText}</p>
    ) : (
        values.map((value, valueIndex) => (
            <div key={valueIndex} className="u-spacing-stack-l">
                {value.map(
                    (item, index) =>
                        item.value && (
                            <div key={index} className="u-spacing-stack-xs">
                                <strong>{element.textfields[index].placeholder}</strong>
                                <div>{item.value}</div>
                            </div>
                        )
                )}
            </div>
        ))
    );

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={listOfValues} />;
};

GroupOfTextFields.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    label: string
): ClaimFormSubmitFieldValue[] => {
    const values: Values = GroupOfTextFields.getValue(formValues, elementId);

    const submitValue: ClaimFormSubmitFieldValue[] = [];

    values.forEach((value, valueIndex) => {
        value.forEach((valueInGroup, valueInGroupIndex) => {
            const submitValueForField = getClaimFormSubmitValue(
                'textfield',
                `${valueInGroup.name}-${valueIndex}-${valueInGroupIndex}`,
                valueInGroup.label,
                valueInGroup.value,
                { string: valueInGroup.value }
            );
            submitValue.push(submitValueForField);
        });
    });
    return submitValue;
};

GroupOfTextFields.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

GroupOfTextFields.getValueOrDefault = (formValues: FormValues, elementId: string): Values => {
    const values = GroupOfTextFields.getValue(formValues, elementId);
    if (values === undefined) {
        /* eslint-disable no-empty-pattern */
        const empty = ([] = []);
        return empty;
    }
    return values;
};

GroupOfTextFields.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = GroupOfTextFields.getValue(formValues, elementId);
    return value;
};

GroupOfTextFields.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    const values: Values = GroupOfTextFields.getValueOrDefault(formValues, elementId);
    const elementConfig = getElementFromElementId(formConfig, elementId) as ListOfItemsType;

    const result = values.map((valueList) =>
        valueList.map((item, i) => (elementConfig.textfields[i].required && item.value === '' ? false : true))
    );
    return result.flat().filter((isValid) => isValid === false).length === 0;
};

export default GroupOfTextFields;
